<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { toast } from 'vue-sonner';
import { dispatchNotification } from '~/lib/notificationDispatch';
import { CLIENT_PORTAL_ROLE_OPTIONS, clientPortalRoleLabel, normalizeClientPortalRole, resolveClientPortalPermissions, type ClientPortalRole } from '~/lib/clientPortalPermissions';
import { buildClientPortalPath } from '~/lib/tenantLinks';
import type { Client, ClientContact, Project } from '~/types/models';

const props = defineProps<{
  open: boolean;
  project?: Project | null;
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'saved'): void;
}>();

const projectStore = useProjectStore();
const clientStore = useClientStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();
const { tenantPath } = useTenantPath();

const message = ref('');
const sendEmail = ref(false);
const saving = ref(false);
const selectedContactKeys = ref<string[]>([]);
const frozenContactSelection = ref<boolean[] | null>(null);
const contactRoleByKey = ref<Record<string, ClientPortalRole>>({});

const canSendPortalEmail = computed(() => ['owner', 'admin'].includes(String(tenantStore.currentTenantRole || '')));
const resolvedClientId = computed(() => {
  const explicitClientId = String(props.project?.clientId || '').trim();
  return explicitClientId;
});
const client = computed<Client | null>(() => {
  if (!resolvedClientId.value) {
    return null;
  }

  return clientStore.clients.find((entry) => entry.id === resolvedClientId.value)
    || (clientStore.currentClient?.id === resolvedClientId.value ? clientStore.currentClient : null)
    || null;
});
const hasClientEmail = computed(() => Boolean(client.value?.email));
const isPublished = computed(() => props.project?.portalShared === true);
const shouldSendEmail = computed(() => sendEmail.value === true);
const clientContacts = computed<ClientContact[]>(() => {
  if (!client.value) return [];
  if (Array.isArray(client.value.contacts) && client.value.contacts.length > 0) {
    return client.value.contacts;
  }

  if (client.value.contactName || client.value.email || client.value.contactNumber || client.value.phone) {
    return [{
      id: `primary-${client.value.id}`,
      name: client.value.contactName || client.value.email || 'Primary contact',
      email: client.value.email || '',
      phone: client.value.contactNumber || client.value.phone || '',
      isPrimary: true,
      projectAccessMode: 'all',
      projectIds: [],
    }];
  }

  return [];
});
const primaryContactKeys = computed(() => new Set(
  clientContacts.value
    .filter((contact) => contact.isPrimary)
    .flatMap((contact) => contactSelectionKeys(contact)),
));
const primaryButtonLabel = computed(() => {
  if (saving.value) {
    return 'Saving...';
  }

  if (isPublished.value) {
    return shouldSendEmail.value ? 'Save And Send' : 'Save Portal Share';
  }

  return shouldSendEmail.value ? 'Publish And Send' : 'Publish';
});

function projectHasContactAccess(contact: ClientContact): boolean {
  if (!props.project?.id) return contact.isPrimary === true;
  if (contact.isPrimary) return true;
  if (contact.projectAccessMode === 'all') return true;
  return Array.isArray(contact.projectIds) && contact.projectIds.includes(props.project.id);
}

function contactSelectionKeys(contact: Partial<ClientContact>): string[] {
  const keys = [
    contact.email ? `email:${String(contact.email).trim().toLowerCase()}` : '',
    contact.id ? `id:${String(contact.id).trim().toLowerCase()}` : '',
    contact.name ? `name:${String(contact.name).trim().toLowerCase()}` : '',
  ];

  return Array.from(new Set(keys.filter(Boolean)));
}

function contactRenderKey(contact: Partial<ClientContact>, index: number): string {
  const email = String(contact.email || '').trim().toLowerCase();
  if (email) return `email:${email}`;

  const id = String(contact.id || '').trim().toLowerCase();
  if (id) return `id:${id}`;

  return `contact:${index}`;
}

function resetSelectedContacts() {
  selectedContactKeys.value = clientContacts.value
    .filter((contact) => projectHasContactAccess(contact))
    .flatMap((contact) => contactSelectionKeys(contact));
  contactRoleByKey.value = Object.fromEntries(
    clientContacts.value.flatMap((contact) => (
      contactSelectionKeys(contact).map((contactKey) => [
        contactKey,
        normalizeClientPortalRole(contact.portalRole, contact.isPrimary === true),
      ])
    )),
  );
}

function handleSendEmailChange(value: boolean | 'indeterminate') {
  sendEmail.value = value === true;
}

function handleContactShareChange(contact: ClientContact, value: boolean | 'indeterminate') {
  if (saving.value || frozenContactSelection.value) {
    return;
  }

  const contactKeys = contactSelectionKeys(contact);
  if (contactKeys.length === 0 || contactKeys.some((contactKey) => primaryContactKeys.value.has(contactKey))) {
    return;
  }

  const next = new Set(selectedContactKeys.value);
  if (value === true) {
    contactKeys.forEach((contactKey) => next.add(contactKey));
  } else {
    contactKeys.forEach((contactKey) => next.delete(contactKey));
  }
  selectedContactKeys.value = Array.from(next);
}

function selectedContactRole(contact: ClientContact): ClientPortalRole {
  const existingKey = contactSelectionKeys(contact).find((contactKey) => contactRoleByKey.value[contactKey]);
  return normalizeClientPortalRole(
    existingKey ? contactRoleByKey.value[existingKey] : contact.portalRole,
    contact.isPrimary === true,
  );
}

function selectedContactRoleLabel(contact: ClientContact): string {
  return clientPortalRoleLabel(selectedContactRole(contact), contact.isPrimary === true);
}

function handleContactRoleChange(contact: ClientContact, value: string) {
  if (saving.value || contact.isPrimary) {
    return;
  }

  const role = normalizeClientPortalRole(value, false);
  const next = { ...contactRoleByKey.value };
  contactSelectionKeys(contact).forEach((contactKey) => {
    next[contactKey] = role;
  });
  contactRoleByKey.value = next;
}

function storedContactHasAccess(contact: ClientContact): boolean {
  const selected = new Set(selectedContactKeys.value);
  return contactSelectionKeys(contact).some((contactKey) => selected.has(contactKey) || primaryContactKeys.value.has(contactKey));
}

function selectedContactHasAccess(contact: ClientContact, index?: number): boolean {
  if (typeof index === 'number' && frozenContactSelection.value) {
    return frozenContactSelection.value[index] === true;
  }

  return storedContactHasAccess(contact);
}

function freezeCurrentContactSelection() {
  frozenContactSelection.value = clientContacts.value.map((contact) => storedContactHasAccess(contact));
}

function contactAccessLabel(contact: ClientContact, index?: number): string {
  if (contact.isPrimary) {
    return 'Full portal access';
  }

  if (selectedContactHasAccess(contact, index)) {
    const count = Array.isArray(contact.projectIds) ? contact.projectIds.length : 0;
    return count > 0 ? `Selected project access (${count})` : 'This project only';
  }

  return 'No access to this project';
}

function buildUpdatedContactsForProjectShare(
  projectId = props.project?.id,
  contacts = clientContacts.value,
  selectedContactKeysSnapshot = selectedContactKeys.value,
  contactRoleByKeySnapshot = contactRoleByKey.value,
): ClientContact[] {
  if (!projectId) {
    return contacts;
  }

  const selected = new Set(selectedContactKeysSnapshot);

  return contacts.map((contact) => {
    if (contact.isPrimary) {
      const portalRole = normalizeClientPortalRole(contact.portalRole, true);
      return {
        ...contact,
        isPrimary: true,
        projectAccessMode: 'all',
        projectIds: [],
        portalRole,
        portalPermissions: resolveClientPortalPermissions({ isPrimary: true, portalRole }),
      };
    }

    const nextProjectIds = new Set(
      (Array.isArray(contact.projectIds) ? contact.projectIds : [])
        .map((id) => String(id || '').trim())
        .filter(Boolean),
    );

    if (contactSelectionKeys(contact).some((contactKey) => selected.has(contactKey))) {
      nextProjectIds.add(projectId);
    } else {
      nextProjectIds.delete(projectId);
    }

    const roleKey = contactSelectionKeys(contact).find((contactKey) => contactRoleByKeySnapshot[contactKey]);
    const portalRole = normalizeClientPortalRole(roleKey ? contactRoleByKeySnapshot[roleKey] : contact.portalRole, false);

    return {
      ...contact,
      projectAccessMode: 'selected',
      projectIds: Array.from(nextProjectIds),
      portalRole,
      portalPermissions: resolveClientPortalPermissions({
        portalRole,
        portalPermissions: contact.portalPermissions,
      }),
    };
  });
}

function closeDialog() {
  emit('update:open', false);
}

watch(
  () => [props.open, props.project?.id] as const,
  async ([open]) => {
    if (!open) {
      return;
    }

    message.value = String(props.project?.portalSummary || '').trim();
    sendEmail.value = false;
    frozenContactSelection.value = null;

    if (clientStore.clients.length === 0) {
      await clientStore.fetchClients();
    }
    if (!client.value && resolvedClientId.value) {
      await clientStore.fetchClientById(resolvedClientId.value);
    }

    resetSelectedContacts();
  },
  { immediate: true },
);

watch(
  () => [client.value?.id, props.project?.id, props.open] as const,
  () => {
    if (props.open && !saving.value) {
      resetSelectedContacts();
    }
  },
);

async function publishToPortal() {
  if (!props.project?.id) {
    return;
  }

  if (!resolvedClientId.value) {
    toast.error('Link this project to a client before publishing it to the portal.');
    return;
  }

  if (shouldSendEmail.value && !canSendPortalEmail.value) {
    toast.error('Only workspace admins and owners can send portal emails right now.');
    return;
  }

  if (shouldSendEmail.value && !hasClientEmail.value) {
    toast.error('The linked client needs an email address before we can send the portal email.');
    return;
  }

  saving.value = true;
  freezeCurrentContactSelection();
  const project = props.project;
  const projectId = project.id;
  const clientId = resolvedClientId.value;
  const clientSnapshot = client.value;
  const contactSnapshot = clientContacts.value.map((contact) => ({ ...contact }));
  const selectedContactKeysSnapshot = [...selectedContactKeys.value];
  const contactRoleByKeySnapshot = { ...contactRoleByKey.value };
  const updatedContacts = clientSnapshot?.id && contactSnapshot.length > 0
    ? buildUpdatedContactsForProjectShare(projectId, contactSnapshot, selectedContactKeysSnapshot, contactRoleByKeySnapshot)
    : [];

  try {
    await projectStore.updateProject(projectId, {
      clientId,
      portalShared: true,
      portalSharedAt: project.portalSharedAt || new Date(),
      portalSharedBy: project.portalSharedBy || userStore.userId || 'unknown',
      portalSummary: message.value.trim(),
    });

    if (clientSnapshot?.id && updatedContacts.length > 0) {
      await clientStore.updateClientContactAccessScopes(clientSnapshot.id, updatedContacts);
    }

    if (shouldSendEmail.value) {
      try {
        const emailResponse = await clientStore.sendProjectLivePortalEmail({
          clientId,
          projectId,
          projectName: project.name,
          projectSummary: message.value.trim() || project.description || undefined,
          dueDate: project.dueDate || undefined,
        });
        console.info('Project portal email queued', {
          projectId,
          clientId,
          response: emailResponse,
        });
      } catch (emailError) {
        console.error('Project was published but the portal email failed:', emailError);
        toast.error(emailError instanceof Error ? emailError.message : 'Project was published, but the client email failed.');
        closeDialog();
        emit('saved');
        return;
      }
    }

    try {
      await dispatchNotification({
        tenantId: String(tenantStore.currentTenantId || ''),
        surface: 'tenant',
        eventType: 'project_published',
        title: `Project live: ${project.name}`,
        body: 'This project is now visible in the client portal.',
        recipients: [{ type: 'tenant_roles', roles: ['owner', 'admin'] }],
        entityType: 'project',
        entityId: projectId,
        clientId,
        projectId,
        actionPath: tenantPath(`/projects/${projectId}`),
        actionLabel: 'Open project',
        actorId: userStore.userId || undefined,
        actorName: userStore.user?.displayName || userStore.user?.fullName,
        actorAvatarURL: userStore.user?.avatarURL,
      });

      if (clientSnapshot?.slug) {
        await dispatchNotification({
          tenantId: String(tenantStore.currentTenantId || ''),
          surface: 'client',
          eventType: 'project_live',
          title: `Project live: ${project.name}`,
          body: message.value.trim() || 'A new project is now ready in your portal.',
          recipients: [{
            type: 'portal_access',
            clientId,
            projectId,
            permission: 'viewProjects',
          }],
          entityType: 'project',
          entityId: projectId,
          clientId,
          projectId,
          actionPath: buildClientPortalPath({
            tenantId: tenantStore.currentTenantId || undefined,
            clientSlug: clientSnapshot.slug,
            section: 'projects',
            resourceId: projectId,
          }),
          actionLabel: 'View project',
          actorId: userStore.userId || undefined,
          actorName: userStore.user?.displayName || userStore.user?.fullName,
          actorAvatarURL: userStore.user?.avatarURL,
        });
      }
    } catch (notificationError) {
      console.warn('Failed to dispatch project publish notifications:', notificationError);
    }

    toast.success(
      isPublished.value
        ? (shouldSendEmail.value ? 'Portal share updated and client emailed.' : 'Portal share updated.')
        : (shouldSendEmail.value ? 'Project published to the client portal and email sent.' : 'Project published to the client portal.'),
    );
    closeDialog();
    emit('saved');
  } catch (error) {
    frozenContactSelection.value = null;
    console.error('Failed to publish project to portal:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to publish project to the client portal.');
  } finally {
    saving.value = false;
  }
}

async function hideFromPortal() {
  if (!props.project?.id) {
    return;
  }

  saving.value = true;
  try {
    await projectStore.updateProject(props.project.id, {
      portalShared: false,
    });
    toast.success('Project hidden from the client portal.');
    closeDialog();
    emit('saved');
  } catch (error) {
    console.error('Failed to hide project from portal:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to hide project from the client portal.');
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <Dialog :open="open" @update:open="(value) => emit('update:open', value)">
    <DialogContent class="max-h-[85vh] overflow-y-auto sm:max-w-xl">
      <DialogHeader>
        <DialogTitle>{{ isPublished ? 'Update Client Portal Share' : 'Publish to Client Portal' }}</DialogTitle>
        <DialogDescription>
          {{ isPublished
            ? 'Refine the client-facing message, resend the notification if needed, or hide the project from the portal.'
            : 'Publish this project when you are ready for the client to see it in their portal.' }}
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4">
        <div class="rounded-xl border border-border/70 bg-muted/20 p-4 text-sm text-muted-foreground">
          <p class="font-medium text-foreground">{{ project?.name || 'Project' }}</p>
          <p class="mt-1">
            {{ client?.name || 'No client linked yet' }}
            <span v-if="client?.email"> - {{ client.email }}</span>
          </p>
        </div>

        <div class="space-y-2">
          <Label for="portal-message">Client portal message</Label>
          <textarea
            id="portal-message"
            v-model="message"
            rows="5"
            class="w-full rounded-xl border border-border/70 bg-background px-3 py-2 text-sm"
            placeholder="Add a short message or overview for the client. This will appear on the project overview page and can also be used in the email."
          />
          <p class="text-xs text-muted-foreground">
            Leave this blank if you want the main project description to carry the overview.
          </p>
        </div>

        <div class="space-y-3 rounded-xl border border-border/70 bg-background px-3 py-3 text-sm">
          <div>
            <p class="font-medium text-foreground">Project access</p>
            <p class="text-muted-foreground">
              Primary contacts always get full portal access. Tick extra contacts who should see this project.
            </p>
          </div>

          <div v-if="clientContacts.length === 0" class="rounded-lg border border-dashed border-border/70 bg-muted/20 p-3 text-muted-foreground">
            No client contacts found. Add contacts to the client before sharing project-specific access.
          </div>

          <div v-else class="space-y-2">
            <label
              v-for="(contact, contactIndex) in clientContacts"
              :key="contactRenderKey(contact, contactIndex)"
              class="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/10 px-3 py-3"
            >
              <Checkbox
                :model-value="selectedContactHasAccess(contact, contactIndex)"
                :disabled="contact.isPrimary || saving"
                class="mt-0.5"
                @update:model-value="(value) => handleContactShareChange(contact, value)"
              />
              <div class="min-w-0 flex-1 space-y-1">
                <div class="flex flex-wrap items-center gap-2">
                  <p class="font-medium text-foreground">{{ contact.name || contact.email || 'Unnamed contact' }}</p>
                  <span v-if="contact.isPrimary" class="rounded-full border border-border/70 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">Primary</span>
                </div>
                <p class="truncate text-muted-foreground">{{ contact.email || 'No email on file' }}</p>
                <p class="text-xs text-muted-foreground">{{ contactAccessLabel(contact, contactIndex) }}</p>
                <div v-if="contact.isPrimary" class="pt-1">
                  <Badge variant="outline">{{ selectedContactRoleLabel(contact) }}</Badge>
                </div>
                <div v-else-if="selectedContactHasAccess(contact, contactIndex)" class="pt-2">
                  <Label :for="`portal-role-${contactRenderKey(contact, contactIndex)}`" class="text-xs text-muted-foreground">
                    Portal role
                  </Label>
                  <SearchableSelectMenu
                    :id="`portal-role-${contactRenderKey(contact, contactIndex)}`"
                    :model-value="selectedContactRole(contact)"
                    :options="CLIENT_PORTAL_ROLE_OPTIONS"
                    :disabled="saving"
                    trigger-class="mt-1"
                    show-all-by-default
                    variant="form"
                    @update:model-value="(value) => handleContactRoleChange(contact, value)"
                  />
                  <p class="mt-1 text-xs text-muted-foreground">
                    {{ CLIENT_PORTAL_ROLE_OPTIONS.find((role) => role.value === selectedContactRole(contact))?.description }}
                  </p>
                </div>
              </div>
            </label>
          </div>
        </div>

        <label class="flex items-start gap-3 rounded-xl border border-border/70 bg-background px-3 py-3 text-sm">
          <Checkbox
            :model-value="sendEmail"
            :disabled="!canSendPortalEmail || !hasClientEmail"
            class="mt-0.5"
            @update:model-value="handleSendEmailChange"
          />
          <div class="space-y-1">
            <p class="font-medium text-foreground">Send email notification</p>
            <p class="text-muted-foreground">
              {{ !canSendPortalEmail
                ? 'Only workspace admins and owners can send the portal email.'
                : !hasClientEmail
                  ? 'Add an email address to the linked client to send the portal email.'
                  : 'Include this message in the project-live email so the client knows the portal is ready.' }}
            </p>
          </div>
        </label>
      </div>

      <DialogFooter class="flex flex-wrap justify-between gap-2 sm:justify-between">
        <Button
          v-if="isPublished"
          type="button"
          variant="outline"
          :disabled="saving"
          @click="hideFromPortal"
        >
          Hide From Portal
        </Button>
        <div v-else />

        <div class="flex flex-wrap gap-2">
          <Button type="button" variant="ghost" :disabled="saving" @click="closeDialog">Cancel</Button>
          <Button type="button" :disabled="saving || !resolvedClientId" @click="publishToPortal">
            {{ primaryButtonLabel }}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
